// Equipment rules: rolling drops, item power, set counting, and the stat source.
import { D, type Decimal } from '../core/decimal';
import type { Rng } from '../core/rng';
import { EQUIPMENT_TUNING as T, RARITIES, SETS, SLOTS, type Rarity, type SetId, type Slot } from '../data/equipment';
import type { GameState } from './state';
import type { ModifierSource } from './stats';

export interface Item {
  uid: number;
  slot: Slot;
  rarity: Rarity;
  /** Stage it dropped on: drives its power. */
  stage: number;
  set?: SetId;
}

export interface EquipmentState {
  items: Item[];
  equipped: Partial<Record<Slot, number>>;
  nextUid: number;
}

export function emptyEquipment(): EquipmentState {
  return { items: [], equipped: {}, nextUid: 1 };
}

const RARITY_BY_ID = new Map(RARITIES.map((r) => [r.id, r]));
const SLOT_BY_ID = new Map(SLOTS.map((s) => [s.id, s]));

export function itemValue(item: Item): Decimal {
  const power = RARITY_BY_ID.get(item.rarity)!.power;
  return D(1 + (power * Math.pow(item.stage, T.stageExp)) / T.stageDiv);
}

/** Relative "how good" score for sorting/auto-compare (ignores set bonuses). */
export function itemScore(item: Item): number {
  return itemValue(item).toNumber();
}

function pickWeighted<T extends { weight: number }>(list: readonly T[], rng: Rng): T {
  const total = list.reduce((s, x) => s + x.weight, 0);
  let r = rng() * total;
  for (const x of list) { r -= x.weight; if (r <= 0) return x; }
  return list[list.length - 1]!;
}

/** Roll a new item for a drop at `stage`. `rarityFloor` guarantees at least that rarity. */
export function rollItem(stage: number, rng: Rng, uid: number, rarityFloor: Rarity = 'common'): Item {
  const floorIdx = RARITIES.findIndex((r) => r.id === rarityFloor);
  const rarity = pickWeighted(RARITIES.slice(floorIdx), rng).id;
  const slot = SLOTS[Math.floor(rng() * SLOTS.length)]!.id;
  const item: Item = { uid, slot, rarity, stage };
  if (stage >= T.setMinStage && rng() < T.setChance) item.set = SETS[Math.floor(rng() * SETS.length)]!.id;
  return item;
}

export function equippedItems(eq: EquipmentState): Item[] {
  const out: Item[] = [];
  for (const uid of Object.values(eq.equipped)) {
    const item = eq.items.find((i) => i.uid === uid);
    if (item) out.push(item);
  }
  return out;
}

export function setCounts(eq: EquipmentState): Map<SetId, number> {
  const counts = new Map<SetId, number>();
  for (const item of equippedItems(eq)) if (item.set) counts.set(item.set, (counts.get(item.set) ?? 0) + 1);
  return counts;
}

export function describeItem(item: Item): string {
  const slot = SLOT_BY_ID.get(item.slot)!;
  return `${slot.statLabel} x${itemValue(item).toNumber().toFixed(2)}`;
}

export function itemName(item: Item): string {
  const slot = SLOT_BY_ID.get(item.slot)!;
  const set = item.set ? SETS.find((s) => s.id === item.set)!.name + ' ' : '';
  return `${RARITY_BY_ID.get(item.rarity)!.name} ${set}${slot.name}`;
}

export function equipmentSource(): ModifierSource {
  return {
    id: 'equipment',
    apply(state: GameState, m) {
      const eq = state.equipment;
      for (const item of equippedItems(eq)) m.mult(SLOT_BY_ID.get(item.slot)!.stat, itemValue(item));
      for (const [setId, count] of setCounts(eq)) {
        const set = SETS.find((s) => s.id === setId)!;
        const bonuses = [...(count >= 3 ? set.three : []), ...(count >= 5 ? set.five : [])];
        for (const b of bonuses) {
          if (b.kind === 'mult') m.mult(b.stat, b.value);
          else m.add(b.stat, b.value);
        }
      }
    },
  };
}
